import { ref, onMounted, computed } from 'vue';
import type { Address } from '@/domains/address';
import {
  getAllAddressesFromCache,
  upsertAddresses,
  getAddressIdsFromCache,
} from '@/domains/address/repositories/addressRepository';
import { apiRequest } from '@/composables/useApi';
import { addressesEndpoints } from '@/services/api/apiConfig';
import { setLoaderConfig } from '@/core/loading/globalLoadingStore';
import type { ApiResponse } from '@/types/api';

const addresses = ref<Address[]>([]);
const error = ref<Error | null>(null);

export const useAddresses = () => {
  // === COMPOSABLE STRUCTURE ===

  // Instance-specific state
  const address = ref<Address | null>(null);

  const isLoadingAddresses = ref(false);

  // Infrastructure helpers
  const fetchAddressesFromApi = async (addressIds: number[]): Promise<Address[]> => {
    if (!addressIds || addressIds.length === 0) {
      return [];
    }

    // Deduplicate IDs
    const uniqueIds = [...new Set(addressIds)];

    // Check which addresses are already in cache
    const cachedAddressIds = await getAddressIdsFromCache();
    const missingIds = uniqueIds.filter((id) => !cachedAddressIds.has(id));

    // If all addresses are already in cache, return empty array (no API call needed)
    if (missingIds.length === 0) {
      return [];
    }

    // Only fetch addresses that are not in cache
    // Since endpoint is dynamic (based on IDs in path), we use apiRequest directly
    // but track loading via ref
    isLoadingAddresses.value = true;
    try {
      const response = await apiRequest<ApiResponse<Address[]>>({
        endpoint: addressesEndpoints.BY_IDS(missingIds),
        method: 'get',
      });

      if (!response?.data) {
        throw new Error('No address data available in API response');
      }

      return response.data;
    } finally {
      isLoadingAddresses.value = false;
    }
  };

  // === BUSINESS LOGIC ===

  // Business Actions

  // Addresses will be loaded automatically when routes/routeStops/shipments are loaded (called in the upsert mechanism)

  const loadAddresses = async (addressIds?: number[]): Promise<void> => {
    // If no IDs provided, just load from cache
    if (!addressIds || addressIds.length === 0) {
      const cachedAddresses = await getAllAddressesFromCache();
      addresses.value = cachedAddresses;
      return;
    }

    error.value = null;

    try {
      const addressCount = addressIds.length;
      const addressText = addressCount > 1 ? 'addresses' : 'address';
      setLoaderConfig({
        variant: 'card',
        message: 'Loading addresses...',
        subtitle: `Retrieving ${addressCount} ${addressText}`,
        size: 'sm',
        backdrop: false,
      });

      const addressesData = await fetchAddressesFromApi(addressIds);

      // If we got new addresses from API, upsert them
      if (addressesData.length > 0) {
        const updatedAddresses = await upsertAddresses(addressesData);
        addresses.value = updatedAddresses;
      } else {
        // No new addresses from API, just refresh from cache
        addresses.value = await getAllAddressesFromCache();
      }
    } catch (e) {
      // API failed - fallback to cache
      console.warn('[useAddresses] API sync failed, falling back to cache:', e);

      const cachedAddresses = await getAllAddressesFromCache();
      if (cachedAddresses && cachedAddresses.length > 0) {
        // Cache has data - use it
        addresses.value = cachedAddresses;
        error.value = null; // Don't set error if we have cache to fallback to
        return;
      }
      // No cache either - this is a real error
      error.value = e instanceof Error ? e : new Error('Failed to fetch addresses');
      throw e;
    }
  };

  // Business Selectors
  const hasAddress = computed(() => address.value != null);

  const getAddressById = (addressId: number): Address | null => {
    return addresses.value.find((a) => a.id === addressId) ?? null;
  };

  // === LIFECYCLE ===

  onMounted(() => {
    void loadAddresses(); // do we need this here?
  });

  // === COMPOSABLE OUTPUT ===

  const state = { address, addresses };

  const selectors = {
    hasAddress,
    getAddressById,
  };

  const actions = {
    loadAddresses,
  };

  const status = {
    isLoading: computed(() => isLoadingAddresses.value),
    error: computed(() => error.value),
  };

  return {
    state,
    selectors,
    actions,
    status,
  };
};
